import React, { useState, useEffect } from 'react';
import { X, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

import { API } from '../config/api';

const ReferralModal = ({ isOpen, onClose }) => {
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchReferralStats();
    }
  }, [isOpen]);

  const fetchReferralStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/referrals/my-stats`, { withCredentials: true });
      setReferralData(response.data);
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    const referralLink = `${window.location.origin}/login?ref=${referralData.referral_code}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors z-10"
        >
          <X className="h-6 w-6 text-gray-500" />
        </button>

        <div className="p-8 pt-12">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <>
              {/* Title */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
                  <svg className="h-8 w-8 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 12v8H4v-8" />
                    <path d="M22 7H2v5h20V7z" />
                    <path d="M12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">Referral Program</h2>
                <p className="text-gray-500 dark:text-gray-400">Invite friends and earn rewards together</p>
              </div>

              {/* Referral Link Section */}
              <div className="mb-8">
                <h3 className="text-purple-600 text-sm font-medium mb-3">Your referral link:</h3>
                <div className="flex items-center gap-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <div className="flex-shrink-0 text-purple-600">
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </div>
                  <div className="flex-1 text-gray-700 dark:text-gray-300 text-sm font-mono overflow-hidden break-all">
                    {window.location.origin}/login?ref={referralData?.referral_code}
                  </div>
                  <Button
                    onClick={copyToClipboard}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-semibold flex items-center gap-2 flex-shrink-0"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Benefits Cards */}
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                {/* Share Card */}
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-800/50 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                        <polyline points="16 6 12 2 8 6" />
                        <line x1="12" y1="2" x2="12" y2="15" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Share your <span className="text-purple-600 font-medium">referral link</span>
                  </p>
                </div>

                {/* Their Benefit Card */}
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-800/50 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    They get <span className="text-purple-600 font-medium">5 credits</span> when they join
                  </p>
                </div>

                {/* Your Benefit Card */}
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-800/50 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 12v8H4v-8" />
                        <path d="M22 7H2v5h20V7z" />
                        <path d="M12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    You earn <span className="text-purple-600 font-medium">40 credits</span> on their first purchase
                  </p>
                </div>
              </div>

              {/* Your Referrals Stats */}
              <div className="mb-6">
                <h3 className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-4">Your referrals:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Referral Credit Earned */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">Referral credit earned</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-200 dark:bg-purple-800/50 rounded-full flex items-center justify-center">
                        <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                      </div>
                      <p className="text-4xl font-bold text-purple-600">{referralData?.total_credits || 0}</p>
                    </div>
                  </div>

                  {/* Total Invitations */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">Total invitations</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-200 dark:bg-purple-800/50 rounded-full flex items-center justify-center">
                        <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </div>
                      <p className="text-4xl font-bold text-purple-600">{referralData?.total_referrals || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms Link */}
              <div className="text-center">
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-500 text-sm hover:text-purple-600 transition-colors inline-flex items-center gap-2"
                >
                  View Terms & Conditions
                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralModal;